<template>
    <Head title="My Work" />
    <MyWorkDemoTools
        :has-insight-feed-flag="insight_feed_flag_enabled"
        :has-integrated-calendar="has_integrated_calendar"
        :has-perspective="has_perspective"
        :has-collaboration-profile="has_collaboration_profile"
        :has-perspective-flag="perspective_assessment_enabled"
        :has-perspective-report-flag="perspective_report_enabled"
        :has-perspective-report="has_perspective_report_row"
        :has-public-profile-flag="public_profile_ff_enabled" />
    <BaseLayout gap="none">
        <BasePageHeader title="My Work" :subtitle="t('myWorkInsightFeed.weeklyDescription')" />

        <div class="flex flex-1 flex-col gap-6">
            <!-- When the sidebar shows, the section heading spans full width above the grid so the
                 main column's first card and the sidebar card start on the same row. -->
            <div v-if="showSidebar && (insightSlots.length || !feed?.exhausted)" class="flex flex-col gap-1">
                <h2 class="title-body text-ink">{{ orderedInsights.length || feed?.exhausted ? insightsSectionHeading : previewHeadingTitle }}</h2>
                <p v-if="!orderedInsights.length && !feed?.exhausted" class="body-small text-ink-muted">{{ previewHeadingSubtitle }}</p>
            </div>

            <!-- Main layout -->
            <div class="grid gap-5 lg:grid-cols-5 lg:items-start">
                <section ref="feedRoot" class="flex flex-col gap-6 order-3 lg:order-2 min-w-0" :class="showSidebar ? 'lg:col-span-3' : 'lg:col-span-5'">
                    <p ref="status" class="sr-only" role="status" aria-live="polite" aria-atomic="true" tabindex="-1">{{ announcement }}</p>
                    <BaseNotification v-if="feedError" theme="error" :text="t('myWorkInsightFeed.error')" @dismiss="feedError = false" />
                    <template v-if="hasIncompleteTiers">
                        <div class="flex flex-col gap-1">
                            <h2 class="title-body text-ink">{{ tier1Complete ? "Connect context to get better insights" : "Connect context to get insights" }}</h2>
                            <p class="body-small text-ink-muted">
                                The more context you give Nadia, the more relevant and useful your insights will be.{{ tier1Complete ? "" : " Start by connecting your calendar." }}
                            </p>
                        </div>
                        <div class="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                            <ConnectContextCard
                                v-for="tier in allEnabledTiers"
                                :key="tier.tier"
                                v-bind="tierCardProps(tier.tier)"
                                :connected="tier.complete"
                                @cta-click="handleTierCta(tier.tier)" />
                        </div>
                    </template>

                    <template v-if="insightSlots.length">
                        <div class="flex flex-col gap-6">
                            <h2 v-if="!showSidebar" class="title-body text-ink">{{ insightsSectionHeading }}</h2>
                            <div>
                                <div v-for="slot in insightSlots" :key="slot.key" :ref="(element) => setInsightSlotElement(slot.key, element)" class="insight-feed-slot">
                                    <Transition
                                        name="insight-feed"
                                        mode="out-in"
                                        @before-leave="holdInsightHeight"
                                        @after-leave="finishInsightFade(slot.key)"
                                        @after-enter="finishInsightTransition">
                                        <MyWorkInsightCardRedesigned
                                            v-if="slot.item"
                                            :key="slot.item.insightWithFeedback.insight.id"
                                            :experiment-title="slot.item.experiment.title"
                                            :experiment-slug="slot.item.experiment.slug"
                                            :title="slot.item.insightWithFeedback.insight.title"
                                            :summary="slot.item.insightWithFeedback.insight.summary"
                                            :content="slot.item.insightWithFeedback.insight.content"
                                            :insight-id="slot.item.insightWithFeedback.insight.id"
                                            :unknowns="slot.item.insightWithFeedback.insight.unknowns"
                                            :evidence-groups="slot.item.insightWithFeedback.insight.evidence_groups"
                                            :initial-feedback-value="slot.item.insightWithFeedback.feedback"
                                            :show-see-more="true" />
                                    </Transition>
                                </div>
                            </div>
                        </div>
                    </template>

                    <!-- No real insights yet: the CTA grid above already covers the still-incomplete
                             case, so this only ever needs the preview. Once everything is connected, the
                             context cards would be a redundant all-"connected" recap, so they're hidden
                             entirely and the preview is all that's shown. -->
                    <MyWorkInsightFeedExhausted v-else-if="feed?.exhausted" />
                    <template v-else>
                        <div class="flex flex-col gap-4">
                            <div v-if="!showSidebar" class="flex flex-col gap-1">
                                <h2 class="title-body text-ink">{{ previewHeadingTitle }}</h2>
                                <p class="body-small text-ink-muted">{{ previewHeadingSubtitle }}</p>
                            </div>
                            <div class="grid gap-5">
                                <MyWorkInsightCardRedesignedPreview
                                    insight-id="example-collaboration-profile"
                                    experiment-title=""
                                    experiment-slug="example-collaboration-profile"
                                    example-label="My Week"
                                    title="Two Teammates, Two Different Paces"
                                    summary="When collaborators on the same project have very different risk tolerances, an insight like this flags the tension early and suggests a conversation instead of letting it play out silently."
                                    :unknowns="['Would a prompt like this actually help you start that conversation?']" />
                                <MyWorkInsightCardRedesignedPreview
                                    insight-id="example-perspective"
                                    experiment-title=""
                                    experiment-slug="example-perspective"
                                    example-label="My Week"
                                    title="Scaling Faster Than the Team Can Absorb"
                                    summary="When hiring velocity outpaces meeting bandwidth and ownership clarity, this insight calls out the risk before it shows up in delivery."
                                    :unknowns="['Is this the kind of early signal that\'s useful to you?']" />
                            </div>
                        </div>
                    </template>
                </section>

                <!-- Right column: Sharing card, below insights on mobile. The full-width heading above
                     the grid keeps this card's top aligned with the main column's first card. -->
                <section v-if="showSidebar" class="flex flex-col gap-6 lg:col-span-2 lg:self-start order-3 min-w-0">
                    <!-- Smart profile-linking suggestions (ENG-1400) -->
                    <!-- Both flags off ⇒ no profile can ever resolve as a suggestion (see effective_access) -->
                    <ProfileLinkSuggestionsCard
                        v-if="showProfileLinkSuggestions"
                        ref="linkProfilesRef"
                        :initial-linked-count="pipeline_linked_profiles_count"
                        :own-profile-share="own_profile_share"
                        :org-has-ms-teams="org_has_ms_teams"
                        :profile-link-sharing-enabled="profile_link_sharing_ff_enabled"
                        :surface="profileLinkSuggestionsCardSurface"
                        @create-profile-shown="handleCreateProfilePromptShown"
                        @create-profile-click="handleCreateProfilePromptClicked" />
                </section>
            </div>
        </div>
    </BaseLayout>

    <CalendarIntegrationSettings
        v-model:open="showCalendarDialog"
        :source="calendarDialogSource"
        :deeplink-source="calendarDialogDeeplinkSource"
        @close="showCalendarDialog = false" />
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router, usePage } from "@inertiajs/vue3";
import { usePreferredReducedMotion } from "@vueuse/core";
import { useFetch } from "~vue/api";
import { dismissNotificationLocallyKey, type PerspectiveReport } from "~vue/appTypes";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import CalendarIntegrationSettings from "~vue/components/CalendarIntegration/CalendarIntegrationSettings.vue";
import MyWorkInsightCardRedesignedPreview from "~vue/components/MyWorkInsightCard.vue";
import { MainMenuPane } from "~vue/components/navigation/menuPanes";
import { useInsightFeedAccessibility } from "~vue/composables/useInsightFeedAccessibility";
import { useInsightFeedSlots } from "~vue/composables/useInsightFeedSlots";
import { useMyWorkInsightFeed } from "~vue/composables/useMyWorkInsightFeed";
import { usePerspectiveReportImport } from "~vue/composables/usePerspectiveReportImport";
import CalendarStarIcon from "~vue/icons/CalendarStarIcon.vue";
import SetupCalendarTile from "~vue/icons/SetupCalendarTile.vue";
import SetupPerspectiveTile from "~vue/icons/SetupPerspectiveTile.vue";
import SetupProfileTile from "~vue/icons/SetupProfileTile.vue";
import ConnectContextCard, { type ConnectContextCardProps } from "~vue/Labs/ConnectContextCard.vue";
import { MYWORK_CTA_SOURCES, TIER_LABELS } from "~vue/Labs/insightCategory";
import { type MyWorkInsightRenderItem, orderMyWorkInsights } from "~vue/Labs/insightOrdering";
import MyWorkDemoTools from "~vue/Labs/MyWorkDemoTools.vue";
import MyWorkInsightCardRedesigned from "~vue/Labs/MyWorkInsightCardRedesigned.vue";
import MyWorkInsightFeedExhausted from "~vue/Labs/MyWorkInsightFeedExhausted.vue";
import ProfileLinkSuggestionsCard from "~vue/Labs/ProfileLinkSuggestionsCard.vue";
import type { OwnProfileShare } from "~vue/Labs/profileLinkTypes";
import type { LabExperiment } from "~vue/Labs/types";
import { useCalendarStore } from "~vue/stores/calendarStore";
import type { CalendarIntegrationViewEvent } from "~vue/types/calendar_integration";
import type { ChatEntranceUseCaseData } from "~vue/types/chat";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { formatDateRange } from "~vue/utils/formatDate";
import { logUserInteraction } from "~vue/utils/logUtils";
import { DateTime } from "luxon";
import { computed, inject, nextTick, onBeforeUnmount, onMounted, provide, ref, shallowRef, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const props = withDefaults(
    defineProps<{
        experiments?: LabExperiment[];
        insight_feed_flag_enabled?: boolean;
        insight_feed_enabled?: boolean;
        insight_feed_exhausted?: boolean;
        calendar_integration?: { enabled?: boolean } | null;
        calendar_demo_data_enabled?: boolean;
        initial_calendar_events?: CalendarIntegrationViewEvent[] | null;
        calendar_coach_use_case?: ChatEntranceUseCaseData | null;
        has_integrated_calendar?: boolean;
        has_perspective?: boolean;
        perspective_report?: PerspectiveReport | null;
        perspective_assessment_enabled?: boolean;
        perspective_report_enabled?: boolean;
        public_profile_ff_enabled?: boolean;
        memory_connections_ff_enabled?: boolean;
        profile_link_sharing_ff_enabled?: boolean;
        org_profile_sharing_ff_enabled?: boolean;
        mywork_profile_link_suggestions_ff_enabled?: boolean;
        pipeline_linked_profiles_count?: number;
        own_profile_share?: OwnProfileShare | null;
        org_has_ms_teams?: boolean;
        has_collaboration_profile?: boolean;
        workiq_ff_enabled?: boolean;
        has_workiq_connected?: boolean;
        workiq_connect_url?: string;
        has_perspective_report_row?: boolean;
    }>(),
    {
        experiments: () => [],
        insight_feed_flag_enabled: false,
        insight_feed_enabled: false,
        insight_feed_exhausted: false,
        calendar_integration: () => ({}),
        calendar_demo_data_enabled: false,
        initial_calendar_events: null,
        calendar_coach_use_case: null,
        has_integrated_calendar: false,
        has_perspective: false,
        perspective_report: null,
        perspective_assessment_enabled: false,
        perspective_report_enabled: false,
        public_profile_ff_enabled: false,
        memory_connections_ff_enabled: false,
        profile_link_sharing_ff_enabled: false,
        org_profile_sharing_ff_enabled: false,
        mywork_profile_link_suggestions_ff_enabled: false,
        pipeline_linked_profiles_count: 0,
        own_profile_share: null,
        org_has_ms_teams: false,
        has_collaboration_profile: false,
        workiq_ff_enabled: false,
        has_workiq_connected: false,
        workiq_connect_url: "/workiq/connect/",
        has_perspective_report_row: false,
    },
);

const page = usePage();

const profileLinkSuggestionsCardSurface = "my_work";

const { t } = useI18n();
const { feed, error: feedError } = useMyWorkInsightFeed(() => ({
    experiments: props.experiments ?? [],
    feed_enabled: props.insight_feed_enabled,
    exhausted: props.insight_feed_exhausted,
}));
const displayedExperiments = computed(() => feed.value?.experiments ?? []);
const flattenedInsights = computed<MyWorkInsightRenderItem[]>(() =>
    displayedExperiments.value.flatMap((experiment) =>
        experiment.insights.map((insightWithFeedback) => ({
            experiment: {
                id: experiment.id,
                slug: experiment.slug,
                title: experiment.title,
            },
            insightWithFeedback,
        })),
    ),
);
const orderedInsights = computed(() =>
    feed.value?.feed_enabled
        ? flattenedInsights.value
              .filter(({ insightWithFeedback }) => insightWithFeedback.insight.made_visible_at != null)
              .sort(
                  (a, b) =>
                      a.insightWithFeedback.insight.order - b.insightWithFeedback.insight.order || a.insightWithFeedback.insight.id.localeCompare(b.insightWithFeedback.insight.id),
              )
        : orderMyWorkInsights(flattenedInsights.value),
);
const { slots: insightSlots, finishLeave: finishInsightLeave } = useInsightFeedSlots(
    () => orderedInsights.value,
    (item) => item.insightWithFeedback.insight.id,
);

const prefersReducedMotion = usePreferredReducedMotion();
const insightSlotElements = new Map<number, HTMLElement>();
const collapseAnimations = new Map<number, Animation>();

function setInsightSlotElement(key: number, element: unknown) {
    if (element instanceof HTMLElement) {
        insightSlotElements.set(key, element);
    } else {
        insightSlotElements.delete(key);
    }
}

onBeforeUnmount(() => {
    for (const animation of collapseAnimations.values()) {
        animation.cancel();
    }
});

async function finishInsightFade(key: number) {
    if (insightSlots.value.find((slot) => slot.key === key)?.item) {
        return;
    }
    const element = insightSlotElements.get(key);
    if (!element || prefersReducedMotion.value === "reduce" || typeof element.animate !== "function") {
        finishInsightLeave(key);
        return;
    }
    const height = `${element.getBoundingClientRect().height}px`;
    const animation = element.animate(
        [
            { height, minHeight: height, marginBottom: getComputedStyle(element).marginBottom },
            { height: "0px", minHeight: "0px", marginBottom: "0px" },
        ],
        { duration: 240, easing: "ease", fill: "forwards" },
    );
    collapseAnimations.set(key, animation);
    try {
        await animation.finished;
        finishInsightLeave(key);
        await nextTick();
    } catch {
        // Unmounting cancels any collapse still in progress.
    } finally {
        collapseAnimations.delete(key);
        animation.cancel();
    }
}

function holdInsightHeight(element: Element) {
    if (element.parentElement) {
        element.parentElement.style.minHeight = `${element.getBoundingClientRect().height}px`;
    }
}

function finishInsightTransition(element: Element) {
    if (element.parentElement) {
        element.parentElement.style.minHeight = "";
    }
    restoreFocus();
}

const feedRoot = shallowRef<HTMLElement | null>(null);
const { announcement, status, restoreFocus } = useInsightFeedAccessibility(
    feedRoot,
    () => orderedInsights.value.map((item) => item.insightWithFeedback.insight.id),
    () => Boolean(feed.value?.exhausted),
);
const calendarIntegrationEnabled = computed(() => Boolean(props.calendar_integration?.enabled));

// The insight batch is generated on a Monday, but a job can finish writing rows with a
// created_at just before midnight (timezone conversion, scheduling jitter), so rolling
// forward to the next Monday on/after the earliest insight recovers the intended week
// even when that timestamp lands a little early.
const insightWeekRangeLabel = computed<string | null>((previous) => {
    if (!orderedInsights.value.length) {
        return previous ?? null;
    }
    const earliestCreatedAt = orderedInsights.value.reduce<DateTime | null>((earliest, { insightWithFeedback }) => {
        const createdAt = DateTime.fromISO(insightWithFeedback.insight.created_at);
        return earliest && earliest <= createdAt ? earliest : createdAt;
    }, null);
    if (!earliestCreatedAt?.isValid) {
        return null;
    }
    const daysUntilMonday = (8 - earliestCreatedAt.weekday) % 7;
    const weekStart = earliestCreatedAt.startOf("day").plus({ days: daysUntilMonday });
    const weekEnd = weekStart.plus({ days: 6 });
    return formatDateRange(weekStart, weekEnd, "month-day");
});
const insightsSectionHeading = computed(() => (insightWeekRangeLabel.value ? t("myWorkInsightFeed.weeklyHeading", { range: insightWeekRangeLabel.value }) : "Your Insights"));

// With no insights the main column shows the activation tiers instead, so sidebar suggestions to
// sharpen insights that don't exist yet has nothing to act on. The fully-connected,
// no-real-insights-yet state is the exception: the main column already shows example preview
// cards there instead of tier CTAs, so the suggestions have the same "what insights could look like"
// context to sit beside. Calendar is the first gate on insights, so profile-linking suggestions
// also wait for it (BRA-871). Both sharing flags off ⇒ no profile can ever resolve as a
// suggestion (see effective_access).
const showProfileLinkSuggestions = computed(
    () =>
        (orderedInsights.value.length > 0 || !hasIncompleteTiers.value) &&
        props.has_integrated_calendar &&
        props.public_profile_ff_enabled &&
        props.memory_connections_ff_enabled &&
        props.mywork_profile_link_suggestions_ff_enabled &&
        (props.org_profile_sharing_ff_enabled || props.profile_link_sharing_ff_enabled),
);

const perspectiveEnabled = computed(() => props.perspective_assessment_enabled || props.perspective_report_enabled);
// When the layout still has Connect-context CTA cards to show (calendar,
// Perspective, or collaboration profiles), the sidebar's own suggestions are redundant with
// those cards, so give the main column the full width instead.
const showSidebar = computed(() => !props.calendar_demo_data_enabled && !hasIncompleteTiers.value && showProfileLinkSuggestions.value);

// Prefer the in-app assessment when its flag is on; otherwise fall back to the
// external Team Tools perspective questionnaire. Only referenced from the tier 2
// CTA, which is itself gated on perspectiveEnabled.
const perspectiveCtaUrl = computed(() => (props.perspective_assessment_enabled ? "/perspective/" : `${page.props.align_host}/perspective/questions`));

// Poll for a Team Tools perspective report in the background. When one is found,
// reload has_perspective so the tier-2 activation card updates without a full refresh.
usePerspectiveReportImport({
    reportEnabled: () => props.perspective_report_enabled,
    assessmentEnabled: () => props.perspective_assessment_enabled,
    initialReport: props.perspective_report,
    onReportFound: () => router.reload({ only: ["has_perspective"] }),
});

provide("calendarIntegration", props.calendar_integration);

const showCalendarDialog = ref(false);
const calendarDialogSource = ref("mywork_activator_cta");
const calendarDialogDeeplinkSource = ref<string | undefined>();
function openCalendarDialog(source: string, deeplinkSource?: string) {
    calendarDialogSource.value = source;
    calendarDialogDeeplinkSource.value = deeplinkSource;
    useCalendarStore.setShowConnectCalendarDialog(true);
    showCalendarDialog.value = true;
}

function handleTier1Cta() {
    logUserInteraction(AmplitudeEventNames.MYWORK_ACTIVATOR_CTA_CLICKED, {
        cta_label: TIER_LABELS[1],
        tier: 1,
        source: MYWORK_CTA_SOURCES.PREVIEW_CARD,
    });
    openCalendarDialog("mywork_activator_cta");
}

function handleTier2Cta() {
    logUserInteraction(AmplitudeEventNames.MYWORK_ACTIVATOR_CTA_CLICKED, {
        cta_label: TIER_LABELS[2],
        tier: 2,
        source: MYWORK_CTA_SOURCES.PREVIEW_CARD,
    });
    globalThis.open(perspectiveCtaUrl.value, "_blank");
}

function handleTier3Cta() {
    logUserInteraction(AmplitudeEventNames.MYWORK_ACTIVATOR_CTA_CLICKED, {
        cta_label: TIER_LABELS[3],
        tier: 3,
        source: MYWORK_CTA_SOURCES.PREVIEW_CARD,
    });
    globalThis.open(tier3Cta.href, "_blank");
}

async function handleTier4Cta() {
    // Await the analytics POST before the same-tab OAuth redirect, or the document unload aborts
    // it and drops the click event. logUserInteraction never rejects.
    await logUserInteraction(AmplitudeEventNames.MYWORK_ACTIVATOR_CTA_CLICKED, {
        cta_label: TIER_LABELS[4],
        tier: 4,
        source: MYWORK_CTA_SOURCES.PREVIEW_CARD,
    });
    globalThis.location.href = props.workiq_connect_url;
}

// ProfileLinkSuggestionsCard bubbles these past itself (it's reused across surfaces), so My Work
// logs its own contextual event name here rather than sharing one with other mount points.
function handleCreateProfilePromptShown() {
    logUserInteraction(AmplitudeEventNames.PROFILE_LINK_CREATE_PROFILE_PROMPT_SHOWN, { surface: profileLinkSuggestionsCardSurface });
}

async function handleCreateProfilePromptClicked() {
    // Await the analytics POST before navigating away, or the same-tab route change can abort it
    // and drop the click event. logUserInteraction never rejects.
    await logUserInteraction(AmplitudeEventNames.PROFILE_LINK_CREATE_PROFILE_PROMPT_CLICKED, { surface: profileLinkSuggestionsCardSurface });
    openUrl(props.has_perspective ? "/profile/create?auto_generate=true" : "/profile/create");
}
const dismissNotificationLocally = inject(dismissNotificationLocallyKey, () => {});

const { execute: recordVisit } = useFetch("/v1/navigation/notifications/dismiss", { immediate: false }).post({ pane: "my_work", track_all_visits: true }).json();

const linkProfilesRef = useTemplateRef("linkProfilesRef");
const LINK_PROFILES_HASH = "#link-profiles";

const scrollToLinkProfileCard = async () => {
    if (globalThis.location.hash === LINK_PROFILES_HASH) {
        await nextTick();
        linkProfilesRef.value?.$el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
};

onMounted(async () => {
    const params = new URLSearchParams(globalThis.location.search);
    if (params.get("connect_calendar") && calendarIntegrationEnabled.value && !props.has_integrated_calendar) {
        // Match homepage campaign attribution, including its limit on caller-provided values.
        openCalendarDialog("deeplink", params.get("source")?.trim().slice(0, 64) || undefined);
    }
    dismissNotificationLocally(MainMenuPane.MY_WORK);
    recordVisit();
    logUserInteraction(AmplitudeEventNames.MYWORK_PAGE_SHOWN, {
        insight_count: orderedInsights.value.length,
        invisible_insight_count: feed.value?.feed_enabled ? flattenedInsights.value.length - orderedInsights.value.length : 0,
        insight_feed_enabled: Boolean(feed.value?.feed_enabled),
        activations: [
            "calendar",
            ...(perspectiveEnabled.value ? ["perspective"] : []),
            ...(props.public_profile_ff_enabled ? ["collab_profile"] : []),
            ...(props.workiq_ff_enabled ? ["workiq"] : []),
        ],
        activation_states: {
            calendar: props.has_integrated_calendar,
            ...(perspectiveEnabled.value ? { perspective: props.has_perspective } : {}),
            ...(props.public_profile_ff_enabled ? { collab_profile: props.has_collaboration_profile } : {}),
            ...(props.workiq_ff_enabled ? { workiq: props.has_workiq_connected } : {}),
        },
    });
    scrollToLinkProfileCard();
});

// Activation completion state
const tier1Complete = computed(() => props.has_integrated_calendar);
const tier2Complete = computed(() => props.has_perspective);
const tier3Complete = computed(() => props.has_collaboration_profile);
const tier4Complete = computed(() => props.has_workiq_connected);

const allEnabledTiers = computed(() =>
    [
        { tier: 1 as const, complete: tier1Complete.value, enabled: true },
        { tier: 2 as const, complete: tier2Complete.value, enabled: perspectiveEnabled.value },
        { tier: 3 as const, complete: tier3Complete.value, enabled: props.public_profile_ff_enabled },
        { tier: 4 as const, complete: tier4Complete.value, enabled: props.workiq_ff_enabled },
    ].filter((tier) => tier.enabled),
);

const hasIncompleteTiers = computed(() => allEnabledTiers.value.some((tier) => !tier.complete));

// Heading shown above the no-real-insights preview; reused as the sidebar's invisible
// alignment spacer so the suggestions card lines up with the first preview card.
const previewHeadingTitle = computed(() => (tier1Complete.value ? "What to expect on Monday" : "Insights you could get"));
const previewHeadingSubtitle = computed(() =>
    tier1Complete.value
        ? "Every Monday, you'll get fresh insights like these from across your work."
        : "Connect more sources above to unlock insights like these. Start by connecting your calendar.",
);

const tierCardProps = (tier: 1 | 2 | 3 | 4) => {
    const cards = {
        1: {
            title: "Calendar Intelligence",
            body: "Connect your calendar to get insights on your key meetings and work rhythm",
            ctaLabel: "Connect calendar",
            iconTile: SetupCalendarTile,
        },
        2: {
            title: "Perspective On You",
            body: "Complete Perspective to get insights on your working style and relationships",
            ctaLabel: "Take the quiz",
            iconTile: SetupPerspectiveTile,
            completedLabel: "Completed",
        },
        3: {
            title: "Collaboration Profile",
            body: "Add Collaboration Profiles to enable insights on team dynamics and initiative patterns",
            ctaLabel: "Create your profile",
            iconTile: SetupProfileTile,
            completedLabel: "Completed",
        },
        4: {
            title: "Work IQ Intelligence",
            body: "Connect Work IQ to unlock insights from your Microsoft 365 signals — email, Teams, and calendar",
            ctaLabel: "Connect Work IQ",
            icon: CalendarStarIcon,
        },
    } satisfies Record<1 | 2 | 3 | 4, Omit<ConnectContextCardProps, "connected">>;
    return cards[tier];
};

function handleTierCta(tier: 1 | 2 | 3 | 4) {
    void { 1: handleTier1Cta, 2: handleTier2Cta, 3: handleTier3Cta, 4: handleTier4Cta }[tier]();
}

const tier3Cta = { label: "Complete Profile", href: "/profile/create" };
</script>

<style scoped>
.insight-feed-slot:not(:last-child) {
    margin-bottom: 1.5rem;
}
.insight-feed-enter-active,
.insight-feed-leave-active {
    transition: opacity 0.18s ease;
}
.insight-feed-enter-from,
.insight-feed-leave-to {
    opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
    .insight-feed-enter-active,
    .insight-feed-leave-active {
        transition: none;
    }
}
</style>
